/**
 * API клиент (параметры приведены в соответствие с names columns БД)
 */

import { cache } from './cache';
import { getConfig } from './config';
import type {
  UUID,
  PaginatedResponse,
  Traverse,
  TraverseFull,
  TraverseInfo,
  Observation,
  ObservationFull,
  Sample,
  SampleFull,
//  InSituResult,
//  LabResult,
  SampleWithInsitu,
  SampleWithLab,
  Expedition,
  Site,
  SiteTimeSeriesData,
  Vessel,
  Lab,
  LabParameter,
  PublicationsData
} from './types';

const config = getConfig('main');
const API_BASE = config.api.baseUrl;

export class ApiError extends Error {
  constructor(public status: number, public statusText: string, public details?: any) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

function validateUUID(id: string): void {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(id)) {
    throw new ApiError(422, 'Invalid UUID format', { id });
  }
}

export async function fetchAPI<T>(endpoint: string, options?: RequestInit, useCache = true): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const method = options?.method || 'GET';
  const cacheKey = `${method}:${url}`;

  if (useCache && method === 'GET' && config.cache.enabled) {
    const cached = cache.get<T>(cacheKey);
    if (cached) return cached;
  }

  let lastError: Error | null = null;
  for (let attempt = 0; attempt < config.api.retryAttempts; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);

      const response = await fetch(url, {
        ...options,
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        let details;
        try { details = await response.json(); } catch { details = await response.text(); }
        throw new ApiError(response.status, response.statusText, details);
      }

      const data = await response.json();
      if (useCache && method === 'GET' && config.cache.enabled) {
        cache.set(cacheKey, data, config.cache.ttl);
      }
      return data;
    } catch (error) {
      lastError = error as Error;
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) break;
      if (attempt < config.api.retryAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
      }
    }
  }
  if (lastError instanceof ApiError) throw lastError;
  throw new ApiError(0, 'Network error', lastError);
}

// --- API Методы ---

export const traversesAPI = {
  list(params?: { status?: 'current' | 'past' | 'all'; vessel?: UUID; expedition?: UUID; limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Traverse>>(`/traverses${qs ? `?${qs}` : ''}`); //`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Traverse>(`/traverses/${id}`);
  },
  getFull(id: UUID) {
    validateUUID(id);
    return fetchAPI<TraverseFull>(`/traverses/${id}/full`);
  },
  getInfo(id: UUID) {
    validateUUID(id);
    return fetchAPI<TraverseInfo>(`/traverses/${id}/info`);
  },
};

export const observationsAPI = {
  list(params?: { traverse?: UUID; site?: UUID; limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Observation>>(`/observations${qs ? `?${qs}` : ''}`);//`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Observation>(`/observations/${id}`);
  },
  getFull(id: UUID) {
    validateUUID(id);
    return fetchAPI<ObservationFull>(`/observations/${id}/full`);
  },
};

export const samplesAPI = {
  list(params?: { observation?: UUID; limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Sample>>(`/samples${qs ? `?${qs}` : ''}`);//`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Sample>(`/samples/${id}`);
  },
  getFull(id: UUID) {
    validateUUID(id);
    return fetchAPI<SampleFull>(`/samples/${id}/full`);
  },
};

export const insituResultsAPI = {
  list(params?: { observation?: UUID; sample?: UUID; parameter?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    // ИЗМЕНЕНО: бэкенд возвращает пробы с вложенными результатами
    return fetchAPI<PaginatedResponse<SampleWithInsitu>>(`/insitu-results${qs ? `?${qs}` : ''}`);//`
  },
};

export const labResultsAPI = {
  list(params?: { observation?: UUID; sample?: UUID; lab?: UUID; parameter?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    // ИЗМЕНЕНО: бэкенд возвращает пробы с вложенными результатами
    return fetchAPI<PaginatedResponse<SampleWithLab>>(`/lab-results${qs ? `?${qs}` : ''}`);//`
  },
};

export const expeditionsAPI = {
  list(params?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Expedition>>(`/expeditions${qs ? `?${qs}` : ''}`);//`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Expedition>(`/expeditions/${id}`);
  },
  getFull(id: UUID) {
    validateUUID(id);
    return fetchAPI<Expedition>(`/expeditions/${id}/full`);
  },
};

export const sitesAPI = {
  list(params?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Site>>(`/sites${qs ? `?${qs}` : ''}`);//`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Site>(`/sites/${id}`);
  },
  getFull(id: UUID) {
    validateUUID(id);
    return fetchAPI<Site>(`/sites/${id}/full`);
  },
  getTimeSeries(id: UUID) {
    validateUUID(id);
    return fetchAPI<SiteTimeSeriesData>(`/data/site/${id}`);
  },
};

export const vesselsAPI = {
  list(params?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Vessel>>(`/vessels${qs ? `?${qs}` : ''}`);//`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Vessel>(`/vessels/${id}`);
  },
  getFull(id: UUID) {
    validateUUID(id);
    return fetchAPI<Vessel>(`/vessels/${id}/full`);
  },
  // НОВОЕ: Загрузка фотографии как Blob (для bytea полей)
  async getPhoto(id: UUID): Promise<string> {
    validateUUID(id);
    const url = `${API_BASE}/vessels/${id}/photo`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Photo not found');
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
 };

export const labsAPI = {
  list(params?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<Lab>>(`/labs${qs ? `?${qs}` : ''}`);//`
  },
  get(id: UUID) {
    validateUUID(id);
    return fetchAPI<Lab>(`/labs/${id}`);
  },
};

export const labParametersAPI = {
  list(params?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams(params as any).toString();
    return fetchAPI<PaginatedResponse<LabParameter>>(`/dict/lab_parameters${qs ? `?${qs}` : ''}`);//`
  },
};

export const publicationsAPI = {
  async list(): Promise<PublicationsData> {
    const response = await fetch('/publications/publications.json');
    if (!response.ok) {
      throw new Error('Failed to load publications');
    }
    return await response.json();
  },
};